import React, { useState, useMemo, useEffect } from "react";
import { tokens } from "@/tokens";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  TextField,
  Typography,
  Alert,
  Skeleton,
  InputAdornment,
  Tooltip,
  Chip,
} from "@mui/material";
import {
  Close,
  Factory as FactoryIcon,
} from "@mui/icons-material";
import InfoIcon from "@/assets/svgs/info.svg";
import { TextInput } from "@/components";
import { useCreateProductMutation, deriveMetalTypeEntries } from "@/services/product-catalogue/pc-api";
import { useLazyCheckMetalPercentageRequiredQuery } from "@/services/product-catalogue/metal-percentage-api";
import { useGetCountrySummaryQuery, useGetHTSCodesQuery } from "@/services/dashboard/sm-api";
import { useAppSelector } from "@/store";
import { roles } from "@/roles";
import { CountrySelect, HTSCodeSelect } from "../product-select-fields";
import { formatHTSCode } from "@/utils";

interface CreateProductFormProps {
  open: boolean;
  onClose: () => void;
  onProductCreated: (productId?: string) => void;
  onTriggerPolling?: (productId: string, triggerReason: 'creation') => void;
  initialData?: {
    htsCode?: string;
    countryOfOrigin?: string;
    metalPercentage?: number;
  };
}

const CreateProductForm: React.FC<CreateProductFormProps> = ({
  open,
  onClose,
  onProductCreated,
  onTriggerPolling,
  initialData,
}) => {
  const { selectedClient } = useAppSelector((state: any) => state.client);
  const { selectedTenant } = useAppSelector((state: any) => state.tenantManagement);
  const { user } = useAppSelector((state: any) => state.auth);

  const [formData, setFormData] = useState({
    sku: "",
    name: "",
    description: "",
    htsCode: "",
    countryOfOrigin: "",
    metalPercentage: "",
  });

  const [showMetalPercentage, setShowMetalPercentage] = useState(false);
  const [metalRules, setMetalRules] = useState<any[]>([]);
  const [metalBreakdownState, setMetalBreakdownState] = useState<Record<string, string>>({});

  const [createProduct, { isLoading }] = useCreateProductMutation();
  const [checkMetalPercentage, { isLoading: isCheckingMetal }] = useLazyCheckMetalPercentageRequiredQuery();

  // Check if TD Admin with TRADEDIVE_CLIENT tenant
  const isTDAdminTCD = useMemo(() => {
    return user?.userRole === roles.TDADMIN && selectedTenant?.tenantType === "TRADEDIVE_CLIENT";
  }, [user, selectedTenant]);

  // Get client ID - properly handle different user roles
  const clientId = useMemo(() => {
    if (isTDAdminTCD) {
      return selectedTenant?._id;
    }
    if (selectedClient?._id) {
      return selectedClient._id;
    }
    if (user?.userRole === roles.TDCLIENT || user?.userRole === roles.SPCLIENT) {
      return user?._id;
    }
    return undefined;
  }, [isTDAdminTCD, selectedTenant, selectedClient, user]);

  // Fetch country data
  const { data: countrySummary, isLoading: isLoadingCountries } = useGetCountrySummaryQuery(
    { clientId: clientId || "" },
    { skip: !clientId || !open }
  );

  // Get all country codes for HTS query when no country is selected
  const allCountryCodes = useMemo(() => {
    if (!countrySummary?.getCountryOriginSummary) return [];
    return countrySummary.getCountryOriginSummary.map(country => country.country_code);
  }, [countrySummary]);

  // Fetch HTS codes based on selected country or all countries
  const { data: htsCodes, isLoading: isLoadingHTS } = useGetHTSCodesQuery(
    {
      clientId: clientId || "",
      country_codes: formData.countryOfOrigin ? [formData.countryOfOrigin] : allCountryCodes,
    },
    { skip: !clientId || !open || allCountryCodes.length === 0 }
  );

  // Check if metal percentage is required when HTS code or country changes
  useEffect(() => {
    const checkMetal = async () => {
      if (formData.htsCode && formData.countryOfOrigin) {
        try {
          const result = await checkMetalPercentage({
            htsCode: formData.htsCode.replace(/\./g, ''),
            countryOfOrigin: formData.countryOfOrigin,
          }).unwrap();

          setShowMetalPercentage(result.isMetalPercentageRequired);
          setMetalRules(result.applicableMetalRules || []);
        } catch (error) {
          console.error("Error checking metal percentage requirement:", error);
          setShowMetalPercentage(false);
          setMetalRules([]);
        }
      } else {
        setShowMetalPercentage(false);
        setMetalRules([]);
      }
    };

    const timer = setTimeout(checkMetal, 500);
    return () => clearTimeout(timer);
  }, [formData.htsCode, formData.countryOfOrigin, checkMetalPercentage]);

  // Multi-metal detection
  const isMultiMetal = metalRules.length > 1;
  const metalTypeEntries = useMemo(() => deriveMetalTypeEntries(metalRules), [metalRules]);

  // Initialize per-metal breakdown defaults when multi-metal is detected
  useEffect(() => {
    if (showMetalPercentage && isMultiMetal) {
      setMetalBreakdownState((prev) => {
        const next = { ...prev };
        metalTypeEntries.forEach(({ key }) => {
          if (!next[key]) next[key] = "";
        });
        return next;
      });
    }
    if (!showMetalPercentage) {
      setMetalBreakdownState({});
    }
  }, [showMetalPercentage, isMultiMetal, metalTypeEntries]);

  // Apply initialData when opening the modal
  useEffect(() => {
    if (open && initialData) {
      setFormData((prev) => ({
        ...prev,
        htsCode: initialData.htsCode ? formatHTSCode(String(initialData.htsCode).replace(/\D/g, "")) : prev.htsCode,
        countryOfOrigin: initialData.countryOfOrigin ?? prev.countryOfOrigin,
        metalPercentage: initialData.metalPercentage !== undefined && initialData.metalPercentage !== null
          ? String(initialData.metalPercentage)
          : prev.metalPercentage,
      }));
    }
  }, [open, initialData]);

  const handleReset = () => {
    setFormData({
      sku: "",
      name: "",
      description: "",
      htsCode: "",
      countryOfOrigin: "",
      metalPercentage: "",
    });
    setShowMetalPercentage(false);
    setMetalRules([]);
    setMetalBreakdownState({});
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleSubmit = async () => {
    try {
      // In multi-metal mode, sum all per-metal percentages as the total metalPercentage
      const metalPercentageValue = isMultiMetal
        ? Object.values(metalBreakdownState).reduce((sum, v) => sum + (Number(v) || 0), 0) || undefined
        : formData.metalPercentage ? Number(formData.metalPercentage) : undefined;

      const result = await createProduct({
        product: {
          sku: formData.sku,
          name: formData.name,
          description: formData.description || undefined,
          htsCode: formData.htsCode,
          countryOfOrigin: formData.countryOfOrigin,
          metalPercentage: metalPercentageValue,
          metalBreakdown: isMultiMetal ? {
            steel: Number(metalBreakdownState.steel) || undefined,
            aluminum: Number(metalBreakdownState.aluminum) || undefined,
            copper: Number(metalBreakdownState.copper) || undefined,
          } : undefined,
          ...(clientId ? { clientId } : {}),
          ...(isTDAdminTCD && { tenantId: selectedTenant?._id }),
        },
      }).unwrap();

      onProductCreated(result._id);

      if (onTriggerPolling && result._id) {
        onTriggerPolling(result._id, 'creation');
      }

      handleClose();
    } catch (error) {
      console.error("Error creating product:", error);
    }
  };

  const isFormValid = () => {
    const baseValid = formData.sku && formData.name && formData.htsCode && formData.countryOfOrigin;

    if (showMetalPercentage && isMultiMetal) {
      const totalMetal = Object.values(metalBreakdownState).reduce((sum, v) => sum + (Number(v) || 0), 0);
      return baseValid && totalMetal <= 100;
    }

    if (showMetalPercentage && formData.metalPercentage) {
      const metalValue = Number(formData.metalPercentage);
      return baseValid && !isNaN(metalValue) && metalValue >= 0 && metalValue <= 100;
    }

    return baseValid;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <Box sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 3,
        py: 2,
        borderBottom: `1px solid ${tokens.border.default}`,
      }}>
        <Box>
          <Typography sx={{ fontWeight: tokens.fontWeight.bold, fontSize: "15px", color: tokens.text.primary }}>
            Add New Product
          </Typography>
          <Typography sx={{ fontSize: tokens.fontSize.base, color: tokens.text.muted, mt: 0.25 }}>
            Create a new product in your catalogue
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" sx={{ color: tokens.text.secondary }}>
          <Close sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {/* Content */}
      <DialogContent sx={{ p: 3, bgcolor: tokens.bg.muted, '& .MuiInputBase-root': { bgcolor: tokens.bg.surface } }}>
        <Stack spacing={2.5}>
          {/* SKU & Name — side by side */}
          <Stack direction="row" spacing={2}>
            <Box sx={{ flex: 1 }}>
              <TextInput
                label="Product SKU"
                type="text"
                value={formData.sku}
                handleChangeValue={(val) => setFormData({ ...formData, sku: val })}
                required
                placeholder="e.g., ELEC-001"
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <TextInput
                label="Product Name"
                type="text"
                value={formData.name}
                handleChangeValue={(val) => setFormData({ ...formData, name: val })}
                required
                placeholder="e.g., Smartphone X12"
              />
            </Box>
          </Stack>

          {/* Description */}
          <TextInput
            label="Description"
            type="text"
            value={formData.description}
            handleChangeValue={(val) => setFormData({ ...formData, description: val })}
            multiline
            rows={2}
            placeholder="Optional product description"
          />

          {/* Country & HTS — side by side */}
          <Stack direction="row" spacing={2}>
            <Box sx={{ flex: 1 }}>
              <CountrySelect
                value={formData.countryOfOrigin}
                onChange={(value) => setFormData({ ...formData, countryOfOrigin: value })}
                countries={countrySummary?.getCountryOriginSummary || []}
                isLoading={isLoadingCountries}
                required
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <HTSCodeSelect
                value={formData.htsCode}
                onChange={(value) => {
                  const formattedValue = formatHTSCode(value.replace(/\D/g, ""));
                  setFormData({ ...formData, htsCode: formattedValue });
                }}
                htsCodes={htsCodes?.getHTSCodesByCountry || []}
                isLoading={isLoadingHTS}
                required
              />
            </Box>
          </Stack>

          {/* Metal Percentage — conditional */}
          {(showMetalPercentage || isCheckingMetal) && (
            <Box sx={{
              p: 2,
              borderRadius: tokens.radius.lg,
              border: "1px solid #fbbf24",
              bgcolor: "#fffbeb",
            }}>
              {isCheckingMetal ? (
                <Stack spacing={1.5}>
                  <Skeleton variant="text" width="180px" height={24} />
                  <Skeleton variant="rectangular" height={40} sx={{ borderRadius: tokens.radius.md }} />
                </Stack>
              ) : (
                <>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                    <FactoryIcon sx={{ color: '#f59e0b', fontSize: 20 }} />
                    <Typography sx={{ fontWeight: tokens.fontWeight.bold, fontSize: tokens.fontSize.md, color: tokens.text.body }}>
                      Metal Content
                    </Typography>
                    <Chip
                      label="Section 232"
                      size="small"
                      color="warning"
                      icon={<img src={InfoIcon} alt="Info" style={{ width: 14, height: 14 }} />}
                      sx={{ height: 22, fontSize: tokens.fontSize.xs }}
                    />
                  </Stack>

                  {metalRules.length > 0 && (
                    <Alert severity="info" sx={{ mb: 1.5, py: 0.5, borderRadius: tokens.radius.md, '& .MuiAlert-message': { fontSize: tokens.fontSize.base } }}>
                      <Typography sx={{ fontSize: tokens.fontSize.base, fontWeight: tokens.fontWeight.semibold, mb: 0.5 }}>
                        Metal percentage required for:
                      </Typography>
                      {metalRules.map((rule, index) => (
                        <Typography key={index} sx={{ fontSize: tokens.fontSize.base, ml: 1 }}>
                          {rule.name} — {(Number(rule.rules.data[0]?.rate) * 100).toFixed(0)}% duty
                        </Typography>
                      ))}
                    </Alert>
                  )}

                  {/* Single metal input */}
                  {!isMultiMetal && (
                    <TextInput
                      label="Metal Percentage (%)"
                      type="number"
                      value={formData.metalPercentage}
                      handleChangeValue={(val) => setFormData({ ...formData, metalPercentage: val })}
                      placeholder="e.g., 85.5"
                      helperText="Percentage of steel/aluminum content (0-100). Leave empty to assume 100%."
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        inputProps: {
                          min: 0,
                          max: 100,
                          step: 0.1
                        }
                      }}
                      error={
                        formData.metalPercentage !== "" &&
                        (Number(formData.metalPercentage) < 0 || Number(formData.metalPercentage) > 100)
                      }
                    />
                  )}

                  {/* Multi-metal inputs */}
                  {isMultiMetal && (
                    <Stack spacing={1.5}>
                      {metalTypeEntries.map(({ key, label }) => (
                        <TextField
                          key={key}
                          label={label}
                          type="number"
                          size="small"
                          value={metalBreakdownState[key] || ""}
                          onChange={(e) => setMetalBreakdownState((prev) => ({ ...prev, [key]: e.target.value }))}
                          placeholder="e.g., 50"
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                            inputProps: { min: 0, max: 100, step: 0.1 },
                          }}
                          sx={{ '& .MuiInputBase-root': { bgcolor: tokens.bg.surface } }}
                        />
                      ))}
                      <Box sx={{ bgcolor: tokens.bg.muted, borderRadius: tokens.radius.sm, px: 1.5, py: 1 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.secondary, fontWeight: tokens.fontWeight.semibold }}>
                            Non-Metal Portion
                          </Typography>
                          <Typography sx={{ fontSize: tokens.fontSize.xs, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary }}>
                            {Math.max(0, 100 - Object.values(metalBreakdownState).reduce((sum, v) => sum + (Number(v) || 0), 0)).toFixed(1)}%
                          </Typography>
                        </Stack>
                        {Object.values(metalBreakdownState).reduce((sum, v) => sum + (Number(v) || 0), 0) > 100 && (
                          <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.status.danger, mt: 0.25 }}>
                            Total metal percentages exceed 100%
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  )}

                  <Tooltip title="The duty rate will be adjusted based on the metal percentage. For example, a 25% duty on 85% steel content = 21.25% effective rate.">
                    <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted, mt: 1, cursor: "help" }}>
                      This field is optional. If not provided, 100% metal content will be assumed.
                    </Typography>
                  </Tooltip>
                </>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>

      {/* Footer */}
      <Box sx={{
        display: "flex",
        justifyContent: "flex-end",
        gap: 1.5,
        px: 3,
        py: 2,
        borderTop: `1px solid ${tokens.border.default}`,
        bgcolor: tokens.bg.surface,
      }}>
        <Button
          onClick={handleClose}
          disabled={isLoading}
          variant="outlined"
          sx={{
            borderColor: tokens.border.default,
            color: tokens.text.body,
            borderRadius: tokens.radius.md,
            
            fontWeight: tokens.fontWeight.semibold,
            fontSize: tokens.fontSize.md,
            height: 38,
            px: 2.5,
            "&:hover": { borderColor: tokens.text.disabled, bgcolor: tokens.bg.hover },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isLoading || !isFormValid()}
          variant="contained"
          startIcon={isLoading ? <CircularProgress size={16} sx={{ color: tokens.bg.surface }} /> : undefined}
          sx={{
            bgcolor: tokens.brand.primary,
            color: tokens.bg.surface,
            borderRadius: tokens.radius.md,
            
            fontWeight: tokens.fontWeight.semibold,
            fontSize: tokens.fontSize.md,
            height: 38,
            px: 2.5,
            boxShadow: "none",
            "&:hover": { bgcolor: tokens.brand.primaryHover, boxShadow: "none" },
            "&.Mui-disabled": { bgcolor: tokens.text.disabled, color: tokens.bg.surface },
          }}
        >
          Create Product
        </Button>
      </Box>
    </Dialog>
  );
};

export default CreateProductForm;
